import { useCallback, useEffect, useState } from 'react';
import { api, session } from '../shell/bridge';
import './SoloSealedStack.css';

/**
 * Card Pn1wrppZMWEr — solo accumulation mode.
 *
 * Pre-pairing, every daily-question answer the user gives stacks as a SEALED
 * card on the solo home: the prompt, the day, a seal, and one count line
 * ("3 answers waiting for your partner"). Waiting reads as accumulation, not
 * absence — the stack only ever grows while there is nobody to reveal to.
 *
 * The unlock moment fires on pairing: when the partner joins, the stack
 * refetches, the server sweeps every deadline-passed sealed question open,
 * and the cards flip into their opened state (answer text, "…is in"). The
 * opened set is held in state so the moment does not evaporate on the next
 * fetch, with a Done that clears it.
 *
 * Answer text never comes back while sealed — the payload carries the prompt
 * and the day only, so this component could not show it if it wanted to.
 */

interface SealedAnswer {
  questionId: string;
  dateKey: string;
  prompt: string;
  answeredAt: number;
}

interface SealedPayload {
  sealed: SealedAnswer[];
  count: number;
  solo: boolean;
  unlocked: {
    question: { questionId: string; dateKey: string; prompt: string };
    answers: { memberId: string; text: string }[];
  }[];
}

type Member = { memberId: string; displayName: string };

/** YYYY-MM-DD → "5 Oct" without touching the timezone (the key is a calendar
 *  day, and a UTC parse would hand back yesterday in front of UTC). */
function dayLabel(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  if (!y || !m || !d) return dateKey;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d} ${months[m - 1]}`;
}

export function SoloSealedStack({ members }: { members: Member[] }) {
  const me = session();
  const [sealed, setSealed] = useState<SealedAnswer[]>([]);
  const [solo, setSolo] = useState(true);
  const [unlocked, setUnlocked] = useState<SealedPayload['unlocked']>([]);
  const [dismissed, setDismissed] = useState(false);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    if (!session()) return; // signed out — there is no stack to read
    try {
      const data = await api<SealedPayload>('/api/questions/sealed');
      setSealed(Array.isArray(data.sealed) ? data.sealed : []);
      setSolo(Boolean(data.solo));
      const fresh = Array.isArray(data.unlocked) ? data.unlocked : [];
      if (fresh.length > 0) {
        setUnlocked(prev => {
          const seen = new Set(prev.map(u => u.question.questionId));
          return [...prev, ...fresh.filter(u => !seen.has(u.question.questionId))];
        });
        setDismissed(false); // new unlocks are the moment — never stay hidden
      }
    } catch (err) {
      // The stack is atmosphere, not a gate: a failed read must never block
      // the solo home, so it renders nothing and logs.
      console.error('[Pear] sealed stack load failed', err);
    } finally {
      setReady(true);
    }
  }, []);

  // `members.length` is the pairing edge: the moment the partner joins, one
  // refetch runs the server-side sweep and brings the unlock back.
  useEffect(() => {
    void load();
  }, [load, members.length]);

  // Count updates: answering today's question (from Us or from Thread's
  // question card) bumps the stack without a reload.
  useEffect(() => {
    const onChange = () => void load();
    window.addEventListener('pear:questions-changed', onChange);
    return () => window.removeEventListener('pear:questions-changed', onChange);
  }, [load]);

  if (!me || !ready) return null;

  const partner = members.find(m => m.memberId !== me.memberId);
  const partnerName = partner?.displayName ?? 'your partner';
  const nameFor = (memberId: string) =>
    memberId === me.memberId ? 'You' : members.find(m => m.memberId === memberId)?.displayName ?? 'Your partner';

  const showSealed = solo && sealed.length > 0;
  /* The unlocked block only exists if somebody actually answered (2026-10-17
     email): an unlock sweep with no answer in it has nothing to reveal, so it
     disappears instead of announcing an empty unlock. Filter per ENTRY, not
     across the stack — a mixed stack (one question answered, another swept
     open with no answer) must not render or count the empty one, and must not
     announce "you're both in" off the union of member ids (pre-deploy review,
     2026-10-17). */
  const visibleUnlocked = unlocked.filter(u => u.answers.length > 0);
  const answererIds = new Set(visibleUnlocked.flatMap(u => u.answers).map(a => a.memberId));
  const everyEntryBothIn = visibleUnlocked.length > 0
    && visibleUnlocked.every(u => new Set(u.answers.map(a => a.memberId)).size > 1);
  const showUnlocked = !dismissed && visibleUnlocked.length > 0;
  if (!showSealed && !showUnlocked) return null;

  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  /* "…is in" claimed the partner was in whatever the answers said — wrong
     whenever they had never answered (2026-10-17 email). Name who actually
     answered: both in, one named person, or a stack where the entries differ. */
  const unlockedHeadline = everyEntryBothIn
    ? `${plural(visibleUnlocked.length, 'answer')} unlocked — you're both in`
    : answererIds.size === 1 && answererIds.has(me.memberId)
      ? `${plural(visibleUnlocked.length, 'answer')} unlocked — you have answered, revealing your response`
      : answererIds.size === 1
        ? `${plural(visibleUnlocked.length, 'answer')} unlocked — ${nameFor([...answererIds][0])} has answered, revealing the response`
        : `${plural(visibleUnlocked.length, 'answer')} unlocked — see who answered what`;

  return (
    <section
      className="pear-sealed-stack"
      data-solo-sealed=""
      data-state={showSealed ? 'sealed' : 'open'}
    >
      {showSealed && (
        <div className="pear-sealed-block" data-sealed-block="">
          <div className="pear-sealed-head">
            <span className="pear-sealed-count" data-sealed-count={sealed.length}>
              {plural(sealed.length, 'answer')} waiting for {partnerName}
            </span>
            <span className="pear-sealed-seal" aria-hidden="true">
              ◆
            </span>
          </div>
          <ul className="pear-sealed-list">
            {sealed.map(s => (
              <li className="pear-sealed-card" key={s.questionId} data-sealed-card={s.dateKey}>
                <span className="pear-sealed-date">{dayLabel(s.dateKey)}</span>
                <p className="pear-sealed-prompt">{s.prompt}</p>
                <span className="pear-sealed-badge">Sealed</span>
              </li>
            ))}
          </ul>
          <p className="pear-sealed-note">They open the moment {partnerName} joins.</p>
        </div>
      )}

      {showUnlocked && (
        <div className="pear-sealed-unlocked" data-unlocked-block="" data-unlocked-count={visibleUnlocked.length}>
          <div className="pear-sealed-head">
            <span className="pear-sealed-count" data-unlocked-count-label={visibleUnlocked.length}>
              {unlockedHeadline}
            </span>
            <button type="button" className="pear-sealed-done" onClick={() => setDismissed(true)}>
              Done
            </button>
          </div>
          <ul className="pear-sealed-list">
            {visibleUnlocked.map(u => (
              <li className="pear-sealed-card is-open" key={u.question.questionId} data-open-card={u.question.dateKey}>
                <span className="pear-sealed-date">{dayLabel(u.question.dateKey)}</span>
                <p className="pear-sealed-prompt">{u.question.prompt}</p>
                {u.answers.map(a => (
                  <p className="pear-sealed-answer" key={a.memberId}>
                    <span className="pear-sealed-who">{nameFor(a.memberId)} said</span>
                    <span className="pear-sealed-line">{a.text}</span>
                  </p>
                ))}
              </li>
            ))}
          </ul>
          <p className="pear-sealed-note">Now in Us → Reveal history.</p>
        </div>
      )}
    </section>
  );
}
